import { LoadingButton } from '@mui/lab'
import { Theme } from '@mui/system'
import { Alert, Divider, FormControlLabel, Grid, Switch } from '@vapor/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHotelDate } from 'src/app/hooks/context/context.hook'
import useGetErrorMessage from 'src/app/hooks/error-message/error-message'
import { GuestOccupancyCheckInWarning } from '../check-in/check.in.types'
import { ReservationContextConsumer, ReservationContextConsumerChildrenParams } from '../../../context/reservation-context-consumer'
import { RRContext } from '../../../context/reservation-context.types'
import { useReservedResourceGuestsForm } from './form/hook'
import GuestForm from './guest/guest'
import AddGuestButton from './components/add-guest-button'
import { ReservedResourcePhase } from 'src/api/api.generated'

export type ReservedResourceGuestsFormProps = {
  readOnly: boolean
  propertyId: string
  reservedResourceId: string
  disableAddGuests?: boolean
  unsavedChanges: (rrId: string | undefined) => void
}

export default function ReservedResourceGuestsForm({ readOnly, propertyId, reservedResourceId, disableAddGuests, unsavedChanges }: ReservedResourceGuestsFormProps) {
  return (
    <div style={{ pointerEvents: readOnly ? 'none' : undefined, border: '1px solid var(--AzureishWhite)', borderLeft: '4px solid var(--DarkImperialBlue)', padding: '10px', paddingBottom: '50px' }}>
      <ReservationContextConsumer reservedResourceId={reservedResourceId} disableAddGuests={disableAddGuests} unsavedChanges={unsavedChanges}>
        {
          //
          (params) => (disableAddGuests ? <></> : <Form {...params} propertyId={propertyId} readOnly={readOnly} />)
        }
      </ReservationContextConsumer>
    </div>
  )
}

function Form({
  readOnly,
  propertyId,
  reservation,
  context,
  reservedResourceContext,
  guestOccupanciesWarnings,
  submitReservedResource,
  submitReduceGuestCount,
  submitAddGuest,
  onGuestChange,
  onCustomerUpserted,
  customerErrorShown,
  guestOccupanciesErrorShown,
  cancelChanges,
  performGuestCheckinCheckout,
  disableAddGuests,
}: ReservationContextConsumerChildrenParams & { propertyId: string; readOnly: boolean }) {
  const { t } = useTranslation()
  const hotelDate = useHotelDate()
  const getErrorMessages = useGetErrorMessage()
  const [internalLoading, setInternalLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [showPastGuests, setShowPastGuests] = useState(false)

  useEffect(() => {
    setLoading(internalLoading || context.loading)
  }, [internalLoading, context.loading])

  const submit = useCallback(async () => {
    setInternalLoading(true)
    await submitReservedResource()
    setInternalLoading(false)
  }, [submitReservedResource])

  const subitRemove = useCallback(
    async (index: number) => {
      setInternalLoading(true)
      await submitReduceGuestCount(index)
      setInternalLoading(false)
    },
    [submitReduceGuestCount],
  )

  const submitAdd = useCallback(
    async (childId: string | null, isReplaceOfCheckoutGuest?: boolean) => {
      setInternalLoading(true)
      await submitAddGuest(childId, isReplaceOfCheckoutGuest)
      setInternalLoading(false)
    },
    [submitAddGuest],
  )
  const { value, errors, submitForm } = useReservedResourceGuestsForm(hotelDate, reservedResourceContext.start, reservedResourceContext.end, reservedResourceContext.guests, submit)

  const hasPastGuests = value.guests?.some((g) => g.phase === 'CHECKOUT')
  const rrCheckout = reservedResourceContext.phase === ReservedResourcePhase.CHECKOUT

  const rrError = useMemo(() => {
    const show = reservedResourceContext.guestOccupanciesError !== null && reservedResourceContext.guestOccupanciesError !== undefined
    const message = getErrorMessages(reservedResourceContext.guestOccupanciesError).join(', ')
    return { show, message }
  }, [reservedResourceContext.guestOccupanciesError])
  return (
    <Grid container spacing={1}>
      {value.guests?.map((g, i) => {
        // doing the filter inside the map so the index is increasing
        if (g.phase === 'CHECKOUT' && !rrCheckout) return null

        return (
          <GuestForm //
            key={i}
            propertyId={propertyId}
            index={i}
            context={context}
            rrContext={reservedResourceContext}
            value={g}
            onChange={(value) => onGuestChange(i, value)}
            onCustomerUpserted={(customer) => onCustomerUpserted(i, customer)}
            onReduceGuestCount={() => subitRemove(i)}
            customerErrorShown={() => customerErrorShown(i)}
            checkInWarning={getCheckInWarning(reservedResourceContext, i, guestOccupanciesWarnings)}
            formErrors={errors.guests?.[i]}
            disabled={loading || readOnly}
            performGuestCheckinCheckout={performGuestCheckinCheckout}
            cityTaxExonerationFromReservation={context.reservation.portalInfo?.portalCityTaxExoneration}
          />
        )
      })}
      {hasPastGuests && !rrCheckout && (
        <Grid item xs={12}>
          <FormControlLabel
            label={t('pms.booking.reservation.guest.show_past_guests')}
            control={
              <Switch
                checked={showPastGuests}
                onChange={(value) => {
                  setShowPastGuests(value.target.checked)
                }}
              />
            }
          />
        </Grid>
      )}
      {showPastGuests &&
        value.guests?.map((g, i) => {
          // doing the filter inside the map so the index is increasing
          if (g.phase !== 'CHECKOUT') return null

          return (
            <GuestForm //
              key={i}
              propertyId={propertyId}
              index={i}
              context={context}
              rrContext={reservedResourceContext}
              value={g}
              onChange={(value) => onGuestChange(i, value)}
              onCustomerUpserted={(customer) => onCustomerUpserted(i, customer)}
              onReduceGuestCount={() => subitRemove(i)}
              customerErrorShown={() => customerErrorShown(i)}
              checkInWarning={getCheckInWarning(reservedResourceContext, i, guestOccupanciesWarnings)}
              formErrors={errors.guests?.[i]}
              disabled={loading || readOnly}
              performGuestCheckinCheckout={performGuestCheckinCheckout}
            />
          )
        })}

      <Grid item xs={12} sx={(theme: Theme) => ({ color: rrError.show ? theme.palette.error.main : undefined })}>
        <Divider light />
      </Grid>

      {rrError.show && (
        <Grid item xs={12}>
          <Alert severity="error" variant="outlined" onClose={guestOccupanciesErrorShown}>
            {rrError.message}
          </Alert>
        </Grid>
      )}

      <Grid item xs={12} container spacing={1}>
        <Grid item xs="auto">
          {disableAddGuests !== true && (
            <AddGuestButton //
              readOnly={readOnly}
              reservation={reservation}
              reservedResourceId={reservedResourceContext.reservedResourceId}
              addGuest={submitAdd}
            />
          )}
        </Grid>
        <Grid item xs />

        {reservedResourceContext.hasChanged && (
          <Grid item xs="auto">
            <LoadingButton disabled={loading || readOnly} variant="outlined" onClick={cancelChanges}>
              {t('pms.booking.reservation.guest.action.undo_changes')}
            </LoadingButton>
          </Grid>
        )}

        {reservedResourceContext.hasChanged && (
          <Grid item xs="auto">
            <LoadingButton loading={loading || readOnly} variant="contained" onClick={submitForm}>
              {t('pms.booking.reservation.guest.action.save_changes')}
            </LoadingButton>
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

function getCheckInWarning(c: RRContext, index: number, warnings: GuestOccupancyCheckInWarning[]): GuestOccupancyCheckInWarning | undefined {
  const guest = c.guests[index]
  if (!guest) return undefined
  const occupancyIndex = guest.occupancyIndex
  if (occupancyIndex === null) return undefined
  return warnings[occupancyIndex]
}
